import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Logo } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { ShieldCheck, XCircle, Search } from "lucide-react";

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return iso?.slice(0, 10); }
}

export default function Verify() {
  const [params] = useSearchParams();
  const [ref, setRef] = useState(params.get("ref") || "");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const check = async (e) => {
    e?.preventDefault();
    if (!ref.trim()) return;
    setLoading(true); setError(""); setResult(null);
    try {
      const { data } = await api.get(`/verify/${encodeURIComponent(ref.trim())}`);
      setResult(data);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "No certificate found for that reference.");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (params.get("ref")) check(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[#0D0F12] px-6 py-16 text-white">
      <div className="mx-auto max-w-lg">
        <Logo wm={28} tone="dark" />
        <h1 className="mt-8 font-heading text-[28px] font-semibold">Certificate verification</h1>
        <p className="mt-2 text-[15px] text-slate-400">
          Enter a certificate reference to confirm it was issued by True Academy. No account required.
        </p>

        <form onSubmit={check} className="mt-6 flex gap-2">
          <input
            data-testid="verify-ref-input"
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            placeholder="e.g. TA-2026-XXXXXXXXXXXX"
            className="h-12 flex-1 rounded-lg border border-white/10 bg-white/5 px-4 text-sm text-white placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-[#33B6E4]"
          />
          <Button type="submit" disabled={loading} className="btn-brand h-12 px-5" data-testid="verify-submit">
            <Search className="mr-2 h-4 w-4" /> Verify
          </Button>
        </form>

        {error && (
          <div className="mt-6 flex items-start gap-3 rounded-xl border border-red-500/30 bg-red-500/10 p-5" data-testid="verify-invalid">
            <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-400" />
            <div>
              <div className="font-semibold">Not verified</div>
              <div className="text-sm text-slate-400">{error}</div>
            </div>
          </div>
        )}

        {result && (
          <div className="mt-6 rounded-xl border border-[#33B6E4]/30 bg-[#33B6E4]/10 p-6" data-testid="verify-valid">
            <div className="flex items-center gap-2 text-[#33B6E4]">
              <ShieldCheck className="h-5 w-5" />
              <span className="font-heading text-[16px] font-semibold">Verified certificate</span>
            </div>
            <dl className="mt-4 space-y-3 text-sm">
              <div className="flex justify-between gap-4"><dt className="text-slate-400">Holder</dt><dd className="font-medium">{result.holder || "-"}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-slate-400">Course</dt><dd className="font-medium text-right">{result.course}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-slate-400">CPD hours</dt><dd className="tnum font-medium">{result.cpd_hours}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-slate-400">Completed</dt><dd className="font-medium">{fmtDate(result.completed_at)}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-slate-400">Reference</dt><dd className="font-mono text-xs">{result.verification_ref}</dd></div>
            </dl>
          </div>
        )}

        <p className="mt-10 text-xs text-slate-600">trueacademy.uk · This page discloses only the holder name, course, CPD hours and completion date.</p>
      </div>
    </div>
  );
}
